import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const EmployeeTeamView = ({ teams, role, loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);

  useEffect(() => {
    // Flatten all employees from all teams and add team info
    const allEmployees = teams.flatMap(team => 
      team.employees?.map(employee => ({
        ...employee,
        teamName: team.team_name,
        teamId: team.team_id
      })) || []
    );

    // Filter employees based on search term and filters
    const filtered = allEmployees.filter(employee => {
      const matchesSearch = !searchTerm || 
        `${employee.name} ${employee.surname} ${employee.email} ${employee.teamName} ${employee.employee_number || ''}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      
      const matchesTeam = selectedTeamId === 'all' || employee.teamId === selectedTeamId;
      const matchesRole = selectedRole === 'all' || employee.role === selectedRole;
      
      return matchesSearch && matchesTeam && matchesRole;
    });

    // Filter teams based on search term
    const filteredTeamsList = teams.filter(team => 
      !searchTerm || 
      team.team_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredEmployees(filtered);
    setFilteredTeams(filteredTeamsList);
  }, [teams, searchTerm, selectedTeamId, selectedRole]);

  // Get unique roles from all employees
  const uniqueRoles = [
    'all', 
    ...new Set(teams.flatMap(team => team.employees?.map(emp => emp.role) || []))
  ];

  const selectedTeamObject = teams.find(team => team.team_id === selectedTeamId);

  const handleRoleClick = (roleToFilter) => {
    setSelectedRole(prevRole => prevRole === roleToFilter ? 'all' : roleToFilter);
  };
  
  const handleTeamClick = (teamId) => {
    setSelectedTeamId(prevTeamId => prevTeamId === teamId ? 'all' : teamId);
  };

  const clearTeamFilter = () => {
    setSelectedTeamId('all');
  };

  return (
    <div className="p-4 bg-white">
      <div className="flex justify-end items-center mb-4"> 
        {/* Search bar only */}
        <div className="relative w-1/3">
          <input
            type="text"
            placeholder="Search employees or teams..."
            className="w-full pl-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

            {/* Main content area */}
      <div className="bg-gray-50 rounded-lg shadow overflow-hidden relative">
        {loading ? (
          <div className="flex items-center justify-center p-8" style={{ height: '600px' }}>
            <div className="text-center">
              <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8 my-1 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading teams and employees...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
            {/* Available Teams Section */}
            <div className="md:col-span-1 bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Available Teams</h3>
              <div className="space-y-2 overflow-y-auto pr-2" style={{ height: '500px' }}>
                {/* Add "All Teams" option */}
                <div 
                  key="all-teams"
                  className={`p-3 rounded border cursor-pointer hover:bg-gray-100 ${
                    selectedTeamId === 'all' ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-300' : 'border-gray-200 bg-white'
                  }`}
                  onClick={() => handleTeamClick('all')}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">All Teams</span>
                  </div>
                </div>
                {filteredTeams.map(team => (
                  <div 
                    key={team.team_id}
                    className={`p-3 rounded border cursor-pointer hover:bg-gray-100 ${
                      selectedTeamId === team.team_id ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-300' : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => handleTeamClick(team.team_id)}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{team.team_name}</span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {team.employees?.length || 0} members
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Members Section */}
            <div className="md:col-span-2 bg-white p-4 rounded-lg border border-gray-200">
              <div className="mb-2">
                <h3 className="text-lg font-semibold">Team Members</h3>
                {/* Role Filter Labels */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Filter by role:</span>
                  {uniqueRoles.map(roleOption => (
                    <button
                      key={roleOption}
                      onClick={() => handleRoleClick(roleOption)}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-150 ease-in-out ${
                        selectedRole === roleOption
                          ? 'bg-purple-500 text-white shadow-md'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {roleOption === 'all' ? 'All' : (roleOption.charAt(0).toUpperCase() + roleOption.slice(1))}
                    </button>
                  ))}
                </div>
              </div>
              {/* Selected Team Indicator */}
              {selectedTeamId !== 'all' && selectedTeamObject && (
                <div className="mb-3 text-sm text-gray-600 bg-purple-50 p-2 rounded inline-flex items-center">
                  Showing members of: <strong className="mx-1">{selectedTeamObject.team_name}</strong>
                  <button 
                    onClick={clearTeamFilter} 
                    className="ml-2 text-purple-500 hover:text-purple-700"
                    title="Show all teams"
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm"/>
                  </button>
                </div>
              )}
              <div className="space-y-3 overflow-y-auto pr-2 dark-gray-scrollbar rounded" style={{ height: '500px' }}>
                {filteredEmployees.map((employee, index) => (
                  <div 
                    key={`${employee.id}-${employee.teamId}-${index}`}
                    className="p-3 bg-white rounded border border-gray-200 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div>
                        <span className="font-medium text-sm">{employee.name} {employee.surname} </span> <span className="text-xs text-gray-400">{employee.employee_number || ''}</span>
                        <span className="ml-2 text-xs text-gray-400">
                          ({employee.teamName})
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          employee.role === 'manager' 
                            ? 'bg-purple-100 text-purple-700' 
                            : employee.role === 'admin' || employee.role === 'company_admin' || employee.role === 'logistics_specialist'
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-700' // Default/employee
                        }`}>
                          {employee.role}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          employee.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {employee.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {employee.email}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {employee.address}
                    </div>
                  </div>
                ))}
                {filteredEmployees.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    No employees match the current filters.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeTeamView; 